/**
 * GlossaryのStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';

import { beginFetch, endFetch, failedFetch } from 'common/modules/app/fetch';
import { fetchFailure, ErrorResponse } from 'common/modules/app/errors';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import { request } from 'common/utils/request/http-request';
import { getGlossaryRequest } from 'common/utils/request/static-request';
import { GetGlossaryResponse } from 'common/utils/request/static-response';
import { saveCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { updateUser, UpdateKeyValue } from 'common/modules/domain/user';

import { AppState } from 'pages';

enum ActionTypes {
  FETCH_SUCCESS = 'app/glossaries/FETCH_SUCCESS'
}

interface Payload {
  glossaries: {
    [key: string]: string;
  };
}

// グロッサリ取得のリクエストID
const GET_GLOSSARY_ID = 'get_glossary';

/**
 * Glossaryの取得成功のActionを作成する
 * @param {GlossaryState} payload Glossary取得のAPIから返されたボディ
 */
export const fetchSuccess = (
  payload: Payload
): { type: ActionTypes; payload: Payload } =>
  createAction(ActionTypes.FETCH_SUCCESS, payload);

type DispatchType = ThunkDispatch<Record<string, unknown>, void, any>;

/**
 * グロッサリファイルを取得する処理
 * @param dispatch
 * @param filename
 * @param language
 * @param isThrowNotFound ファイルが取得できない場合に例外を投げるか否か
 */
const get = async (
  dispatch: DispatchType,
  filename: string,
  language?: string,
  isThrowNotFound?: boolean
) => {
  // 該当リクエストIDの状態を処理中に変更
  dispatch(beginFetch(GET_GLOSSARY_ID));
  const requestData = getGlossaryRequest(filename, language);
  const response = await request(requestData.url, requestData.config);
  // API実行結果がエラーかどうか判断
  if (response.error) {
    if (response.error.statusCode === 403 && isThrowNotFound) {
      throw response.error;
    }
    // エラー処理
    const error = response.error as ErrorResponse;
    error.requestId = GET_GLOSSARY_ID;
    dispatch(fetchFailure(error));
    dispatch(failedFetch(GET_GLOSSARY_ID));
    return;
  } else {
    // APIが成功した場合は取得したグロッサリでstateを更新
    const body = (response.body as unknown) as GetGlossaryResponse;
    dispatch(fetchSuccess({ glossaries: body }));
  }
  // 該当リクエストIDの状態を未処理に変更
  dispatch(endFetch(GET_GLOSSARY_ID));
};

/**
 * グロッサリを取得する
 * @param {string} filename 取得するグロッサリのファイル名
 */
export const getGlossary = (
  filename: string
): ((dispatch: DispatchType, getState: () => AppState) => Promise<void>) => {
  return async (dispatch: DispatchType, getState: () => AppState) => {
    const state = getState();
    const language = state.domain.user.language;
    await get(dispatch, filename, language);
  };
};

/**
 * 選択された言語の全グロッサリを取得する
 *
 * ユーザーStateの言語が言語マスターにない場合は、SAS非対応言語のため英語で表示する
 */
export const getGlossaryAll = (): ((
  dispatch: DispatchType,
  getState: () => AppState
) => Promise<void>) => {
  return async (dispatch: DispatchType, getState: () => AppState) => {
    const state = getState();
    const language = state.domain.user.language;
    const languageMaster = state.app.master.language;

    // 言語マスターにない場合は、SAS非対応言語のため英語で表示する
    const displayLang = languageMaster[language]
      ? language
      : (() => {
          saveCookie(SasCookieKeys.KEY_LANGUAGE, 'en', 180);
          dispatch(updateUser(UpdateKeyValue.KEY_LANGUAGE, 'en'));
          return 'en';
        })();

    await Promise.all([
      get(dispatch, 'language'),
      get(dispatch, 'common', displayLang),
      get(dispatch, 'board', displayLang),
      get(dispatch, 'planList', displayLang),
      get(dispatch, 'planDetail', displayLang),
      get(dispatch, 'errors', displayLang),
      get(dispatch, 'contract', displayLang),
      get(dispatch, 'history', displayLang),
      get(dispatch, 'completion', displayLang)
    ]);
  };
};

const actions = {
  fetchSuccess
};
type GlossaryAction = ActionsUnion<typeof actions>;

export interface GlossaryState {
  /** Glossary。キーがGlossaryID、値が文言。 */
  glossaries: {
    [key: string]: string;
  };
}

const initialState: GlossaryState = {
  glossaries: {}
};

export default (
  state: GlossaryState = initialState,
  actions: GlossaryAction
): GlossaryState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS:
      return {
        glossaries: {
          ...state.glossaries,
          ...actions.payload.glossaries
        }
      };
    default:
      return state;
  }
};
